import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Calendar, Settings as SettingsIcon, CheckCircle2, XCircle, LogIn } from 'lucide-react';
import Navigation from '../components/Navigation';
import MyMeetings from './MyMeetings';
import Settings from './Settings';
import { getAuthHeaders, isAuthenticated } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigiCalendar = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('meetings'); // 'meetings' or 'settings'
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleCalendarInfo, setGoogleCalendarInfo] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const userIsAuthenticated = isAuthenticated();

  useEffect(() => {
    // Check for OAuth success/error in URL
    const params = new URLSearchParams(window.location.search);
    const googleConnected = params.get('google_connected');
    const error = params.get('error');
    
    if (googleConnected === 'true') {
      toast.success('Google Calendar connected successfully!');
      // Clean up URL
      window.history.replaceState({}, '', '/digi-calendar');
    } else if (error) {
      toast.error('Failed to connect Google Calendar. Please try again.');
      window.history.replaceState({}, '', '/digi-calendar');
    }
    
    if (userIsAuthenticated) {
      checkGoogleCalendarStatus();
    } else {
      setCheckingStatus(false);
    }
  }, [userIsAuthenticated]);

  const checkGoogleCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/status`, {
        headers: getAuthHeaders()
      });
      setGoogleConnected(response.data.connected);
      if (response.data.connected) {
        setGoogleCalendarInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to check Google Calendar status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const connectGoogleCalendar = async () => {
    if (!userIsAuthenticated) {
      toast.error('Please login to connect Google Calendar');
      navigate('/auth');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/calendar/google/connect`, {
        headers: getAuthHeaders()
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Google Calendar connection error:', error);
      toast.error('Failed to connect Google Calendar');
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar? Your DigiCalendar will continue working, but availability sync will stop.')) return;
    
    try {
      await axios.delete(`${API}/calendar/google/disconnect`, {
        headers: getAuthHeaders()
      });
      toast.success('Google Calendar disconnected');
      setGoogleConnected(false);
      setGoogleCalendarInfo(null);
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Digi Calendar
          </h1>
          <p className="text-slate-600">Manage your meetings and availability settings</p>
        </div>

        {/* Google Calendar Integration Banner */}
        <Card className={`p-6 mb-6 border-2 ${googleConnected ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${googleConnected ? 'bg-green-100' : 'bg-blue-100'}`}>
                <Calendar className={`w-6 h-6 ${googleConnected ? 'text-green-600' : 'text-blue-600'}`} />
              </div>
              <div>
                {checkingStatus ? (
                  <p className="text-sm text-slate-600">Checking Google Calendar connection...</p>
                ) : googleConnected ? (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <h3 className="text-lg font-semibold text-green-900">
                        Google Calendar Connected
                      </h3>
                    </div>
                    <p className="text-sm text-green-800">
                      {googleCalendarInfo?.calendar_name} • Syncing automatically to prevent double bookings
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-blue-900 mb-1">
                      Connect Google Calendar
                    </h3>
                    <p className="text-sm text-blue-800">
                      Sync your Google Calendar with DigiCalendar to automatically block busy times and prevent double bookings
                    </p>
                  </>
                )}
              </div>
            </div>
            {!checkingStatus && (
              <div>
                {googleConnected ? (
                  <Button
                    variant="outline"
                    onClick={disconnectGoogleCalendar}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    onClick={connectGoogleCalendar}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Connect Now
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Tab Navigation */}
        <Card className="p-2 mb-6 bg-white shadow-sm inline-flex gap-2">
          <Button
            variant={activeTab === 'meetings' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('meetings')}
            className={activeTab === 'meetings' ? 'bg-slate-900' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            My Meetings
          </Button>
          <Button
            variant={activeTab === 'settings' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('settings')}
            className={activeTab === 'settings' ? 'bg-slate-900' : ''}
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Availability Settings
          </Button>
        </Card>

        {/* Tab Content */}
        <div>
          {!userIsAuthenticated ? (
            <Card className="p-8 text-center">
              <div className="max-w-md mx-auto">
                <LogIn className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Login Required</h2>
                <p className="text-slate-600 mb-6">
                  Please login to access your meetings and availability settings
                </p>
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Login / Register
                </Button>
              </div>
            </Card>
          ) : (
            <>
              {activeTab === 'meetings' ? (
                <MyMeetings embedded={true} />
              ) : (
                <Settings embedded={true} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigiCalendar;
